import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithSessionHandling } from '../utils/authFetch';
import { parseApiDateTime } from '../utils/apiDateTime';

interface UserStat {
  id: string;
  email: string;
  hospitalName?: string | null;
  role: string;
  isViewer: boolean;
  disabled: boolean;
  fileCount: number;
  dataVolume: number;
  lastLoginAt?: string | null;
}

type SortKey = 'role' | 'lastLoginAt' | 'fileCount' | 'dataVolume';
type SortOrder = 'asc' | 'desc';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const JST_TIME_ZONE = 'Asia/Tokyo';

const roleOrder: Record<string, number> = {
  管理者: 0,
  VIP: 1,
  試用: 2,
  通常: 3,
  期限切れ: 4,
  閲覧用: 5,
};

const roleLabels: Record<string, string> = {
  管理者: '管理者',
  VIP: 'VIP',
  試用: '試用',
  通常: '通常',
  期限切れ: '期限切れ',
  閲覧用: '閲覧用',
};

const DbAdminView: React.FC = () => {
  const { token, logout } = useAuth();
  const [stats, setStats] = useState<UserStat[]>([]);
  const [orderBy, setOrderBy] = useState<SortKey>('role');
  const [order, setOrder] = useState<SortOrder>('asc');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const fetchStats = async () => {
    const authToken = token;
    const { response, sessionExpired } = await fetchWithSessionHandling(
      `${API_BASE_URL}/api/admin/db/user-stats`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      },
      { logout },
    );
    if (sessionExpired) {
      return;
    }
    if (!response.ok) {
      setSnackbar({ open: true, message: '読み込みに失敗しました', severity: 'error' });
      return;
    }
    const data = await response.json();
    setStats(data);
    setSnackbar({ open: true, message: '読み込みました', severity: 'success' });
  };

  const handleSort = (key: SortKey) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrder('asc');
    }
  };

  const sortedStats = useMemo(() => {
    const sorted = [...stats];
    sorted.sort((a, b) => {
      let aValue: number | string | null = null;
      let bValue: number | string | null = null;

      switch (orderBy) {
        case 'role':
          aValue = roleOrder[a.role] ?? Number.MAX_SAFE_INTEGER;
          bValue = roleOrder[b.role] ?? Number.MAX_SAFE_INTEGER;
          break;
        case 'lastLoginAt': {
          const aTime = parseApiDateTime(a.lastLoginAt)?.getTime() ?? 0;
          const bTime = parseApiDateTime(b.lastLoginAt)?.getTime() ?? 0;
          aValue = aTime;
          bValue = bTime;
          break;
        }
        case 'fileCount':
          aValue = a.fileCount;
          bValue = b.fileCount;
          break;
        case 'dataVolume':
          aValue = a.dataVolume;
          bValue = b.dataVolume;
          break;
        default:
          break;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue ?? '');
      const bStr = String(bValue ?? '');
      return order === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
    return sorted;
  }, [stats, orderBy, order]);

  const userCount = useMemo(() => stats.length, [stats]);

  const totalDataVolume = useMemo(
    () => stats.reduce((sum, user) => sum + (Number.isFinite(user.dataVolume) ? user.dataVolume : 0), 0),
    [stats]
  );

  const eligibleUserCount = useMemo(
    () => stats.filter((user) => !user.isViewer).length,
    [stats]
  );

  const averageDataVolume = useMemo(() => {
    if (eligibleUserCount === 0) {
      return 0;
    }
    const eligibleTotal = stats.reduce((sum, user) => {
      if (user.isViewer) {
        return sum;
      }
      return sum + (Number.isFinite(user.dataVolume) ? user.dataVolume : 0);
    }, 0);
    return eligibleTotal / eligibleUserCount;
  }, [eligibleUserCount, stats]);

  const formatLastLogin = (value?: string | null) => {
    if (!value) return '-';
    try {
      const date = parseApiDateTime(value);
      if (!date) return value;
      return new Intl.DateTimeFormat('ja-JP', {
        timeZone: JST_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      return value;
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formatDataVolume = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes < 0) {
      return '-';
    }
    const megabytes = bytes / (1024 * 1024);
    const formatter = new Intl.NumberFormat('ja-JP', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
    return `${formatter.format(megabytes)} MB`;
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        DB閲覧
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" onClick={fetchStats}>
          再読み込み
        </Button>
      </Box>
      <Typography variant="subtitle1">ユーザー数: {userCount.toLocaleString()}</Typography>
      <Typography variant="subtitle1">総データ量: {formatDataVolume(totalDataVolume)}</Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        データ量平均: {formatDataVolume(averageDataVolume)} (集計対象：{eligibleUserCount.toLocaleString()} ユーザー)
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>メールアドレス</TableCell>
            <TableCell>病院名</TableCell>
            <TableCell sortDirection={orderBy === 'role' ? order : false}>
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={orderBy === 'role'}
                direction={orderBy === 'role' ? order : 'asc'}
                onClick={() => handleSort('role')}
              >
                権限
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'fileCount' ? order : false}>
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={orderBy === 'fileCount'}
                direction={orderBy === 'fileCount' ? order : 'asc'}
                onClick={() => handleSort('fileCount')}
              >
                ファイル数
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'dataVolume' ? order : false}>
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={orderBy === 'dataVolume'}
                direction={orderBy === 'dataVolume' ? order : 'asc'}
                onClick={() => handleSort('dataVolume')}
              >
                データ量
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'lastLoginAt' ? order : false}>
              <TableSortLabel
                sx={{ '& .MuiTableSortLabel-icon': { opacity: 1, color: 'inherit' } }}
                active={orderBy === 'lastLoginAt'}
                direction={orderBy === 'lastLoginAt' ? order : 'asc'}
                onClick={() => handleSort('lastLoginAt')}
              >
                最終ログイン日時
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedStats.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.id.slice(0, 8)}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.hospitalName || '-'}</TableCell>
              <TableCell>{roleLabels[user.role] ?? user.role}</TableCell>
              <TableCell>{user.fileCount.toLocaleString()}</TableCell>
              <TableCell>{formatDataVolume(user.dataVolume)}</TableCell>
              <TableCell>{formatLastLogin(user.lastLoginAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DbAdminView;
